// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Outsize — bootstrap and the screen machine. Owns which screen is up, the room's
// Net (created ONCE per session, never left for a rematch), and the glue between the
// pure game/Race and the DOM. Rules live in game.ts; the board in render.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { newSeed } from '@ben-gy/game-engine/rng';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  normalizeRoomCode,
  setRoomInUrl,
} from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { escapeHtml } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { STATS } from './data';
import {
  biggerSide,
  correctSide,
  createGame,
  current,
  guess as applyGuess,
  type GameState,
  type Side,
} from './game';
import { dailySeed, todayUTC, type Pair } from './seed';
import { createBoard, REVEAL_MS, type Board } from './render';
import { Race, seatOf, createOneShot, type RaceMsg } from './race';
import { runCountdown, type Countdown } from './countdown';
import { aboutHtml, ATTRIB, helpHtml, menuHtml, multiResultsHtml, soloResultsHtml } from './ui';

const SLUG = 'outsize';

// ── shell ───────────────────────────────────────────────────────────────────

const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

// ── persistent session state ─────────────────────────────────────────────────

let playerName = resolveName(store, () => `Player ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;

function bestFor(modeId: string): number {
  return store.get<number>(`best_${modeId}`, 0) ?? 0;
}
function setBest(modeId: string, v: number): void {
  if (v > bestFor(modeId)) store.set(`best_${modeId}`, v);
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let race: Race | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';

// ── screen lifecycle ─────────────────────────────────────────────────────────

const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `
    <main class="main-content" id="content"></main>
    <footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── menu ─────────────────────────────────────────────────────────────────────

function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({
    modeId: myModeChoice,
    best: bestFor(myModeChoice),
    muted: sfx.muted(),
    dateStr: todayUTC(),
  });

  content.querySelectorAll<HTMLElement>('.chip').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = c.dataset.mode!;
      store.set('mode', myModeChoice);
      showMenu();
    }),
  );
  content.querySelector('#playDaily')!.addEventListener('click', () => startSolo(true));
  content.querySelector('#playRandom')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#playFriends')!.addEventListener('click', showRoomEntry);
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── the game screen (shared by solo + multiplayer) ────────────────────────────

interface Outcome {
  pair: Pair;
  picked: Side | null;
  correct: boolean;
  over: boolean;
  streak: number;
}

interface GameConfig {
  mode: Mode;
  timed: boolean;
  state: () => GameState;
  msLeft: () => number | null;
  /** Apply a pick; returns the answered pair + result. */
  applyPick: (side: Side) => Outcome;
  /** Versus: advance the clock; returns an Outcome if it caused a timeout, else null. */
  tickModel: (dt: number) => Outcome | null;
  onOver: () => void;
  onQuit: () => void;
}

interface GameScreen {
  destroy(): void;
}

function feedback(o: { correct: boolean; over: boolean; streak: number }): void {
  try {
    if (o.correct) {
      if (o.streak > 0 && o.streak % 5 === 0) sfx.play('win');
      else sfx.play('coin', { pitch: 0.7 + Math.min(1, o.streak / 20) * 1.0 });
    } else {
      sfx.play('hurt');
    }
  } catch {
    /* audio best-effort */
  }
  if (!reducedMotion && 'vibrate' in navigator) {
    try {
      navigator.vibrate(o.correct ? 12 : [20, 30, 20]);
    } catch {
      /* haptics best-effort */
    }
  }
}

function buildGameScreen(cfg: GameConfig): GameScreen {
  const mode = cfg.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <span class="hud-streak" id="hudStreak">Streak 0</span>
        <button class="linkbtn hud-quit" id="quit">Quit</button>
      </div>
      <div class="timer-wrap" id="timerWrap" hidden><span class="timer-bar" id="timerBar"></span></div>
      <div class="board-host" id="boardHost"></div>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const hudStreak = content.querySelector<HTMLElement>('#hudStreak')!;
  const timerWrap = content.querySelector<HTMLElement>('#timerWrap')!;
  const timerBar = content.querySelector<HTMLElement>('#timerBar')!;
  content.querySelector('#quit')!.addEventListener('click', cfg.onQuit);

  let revealing = false;
  let destroyed = false;
  let revealTimer: ReturnType<typeof setTimeout> | null = null;

  const board: Board = createBoard(boardHost, (side) => onPick(side));

  function renderAsk(): void {
    const st = cfg.state();
    const p = current(st);
    if (!p) return;
    hudStreak.textContent = `Streak ${st.streak}`;
    hudStreak.classList.toggle('hot', st.streak >= 5);
    board.render({ pair: p, statLabel: STATS[p.stat].label, phase: 'ask', reducedMotion });
    updateTimer();
  }

  function updateTimer(): void {
    const ms = cfg.timed ? cfg.msLeft() : null;
    if (ms === null) {
      timerWrap.hidden = true;
      return;
    }
    timerWrap.hidden = false;
    const frac = Math.max(0, Math.min(1, ms / mode.pairMs));
    timerBar.style.width = `${(frac * 100).toFixed(1)}%`;
    timerBar.classList.toggle('low', frac <= 0.3);
  }

  function showReveal(o: Outcome): void {
    revealing = true;
    hudStreak.textContent = `Streak ${o.correct ? o.streak : Math.max(0, o.streak)}`;
    board.render({
      pair: o.pair,
      statLabel: STATS[o.pair.stat].label,
      phase: 'reveal',
      pickedSide: o.picked,
      correctSide: biggerSide(o.pair),
      reducedMotion,
    });
    if (!o.correct && !reducedMotion) {
      boardHost.classList.remove('shake');
      void boardHost.offsetWidth;
      boardHost.classList.add('shake');
    }
    revealTimer = setTimeout(() => {
      revealing = false;
      if (destroyed) return;
      if (o.over) cfg.onOver();
      else renderAsk();
    }, REVEAL_MS);
  }

  function onPick(side: Side): void {
    if (revealing || destroyed) return;
    const st = cfg.state();
    if (st.over) return;
    const o = cfg.applyPick(side);
    feedback({ correct: o.correct, over: o.over, streak: o.streak });
    showReveal(o);
  }

  function onKey(e: KeyboardEvent): void {
    if (revealing) return;
    if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A' || e.key === '1') onPick('left');
    else if (e.key === 'ArrowRight' || e.key === 'l' || e.key === 'L' || e.key === '2') onPick('right');
  }
  window.addEventListener('keydown', onKey);

  // The interval loop: versus timer only. Paused while a reveal is on screen so the
  // next pair's clock never ticks down before the player sees it.
  const loop = cfg.timed
    ? setInterval(() => {
        if (revealing || destroyed) return;
        const o = cfg.tickModel(250);
        if (o) {
          feedback({ correct: false, over: o.over, streak: o.streak });
          showReveal(o);
        } else {
          updateTimer();
        }
      }, 250)
    : null;

  renderAsk();

  return {
    destroy() {
      destroyed = true;
      if (revealTimer) clearTimeout(revealTimer);
      if (loop) clearInterval(loop);
      window.removeEventListener('keydown', onKey);
      board.destroy();
    },
  };
}

/** Capture the outcome of picking `side` on `before` (answered pair + result). */
function outcome(before: GameState, side: Side | null): Outcome {
  const pair = current(before)!;
  return {
    pair,
    picked: side,
    correct: side !== null && side === biggerSide(pair),
    over: false,
    streak: before.streak,
  };
}

// ── solo ─────────────────────────────────────────────────────────────────────

let soloState: GameState | null = null;
let soloDaily = false;
let soloScreen: GameScreen | null = null;

function startSolo(daily: boolean): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = daily;
  const seed = daily ? dailySeed(mode, todayUTC()) : newSeed();
  soloState = createGame(mode, seed, false);

  soloScreen = buildGameScreen({
    mode,
    timed: false,
    state: () => soloState!,
    msLeft: () => null,
    applyPick: (side) => {
      const before = soloState!;
      const o = outcome(before, side);
      soloState = applyGuess(before, side);
      o.over = soloState.over;
      o.streak = soloState.streak;
      return o;
    },
    tickModel: () => null,
    onOver: () => endSolo(daily),
    onQuit: () => showMenu(),
  });
  onCleanup(() => soloScreen?.destroy());
  runCountdown(document.body, sfx, () => {});
}

function endSolo(daily: boolean): void {
  const st = soloState!;
  const mode = modeOf(st.modeId);
  if (!daily) setBest(mode.id, st.streak);
  else if (st.streak > bestFor(mode.id)) setBest(mode.id, st.streak);
  showSoloResults();
}

function showSoloResults(): void {
  teardown();
  setPlaying(false);
  const st = soloState!;
  const mode = modeOf(st.modeId);
  content.innerHTML = `
    ${soloResultsHtml(st, mode, bestFor(mode.id), soloDaily)}
    <div class="menu-actions">
      <button class="btn primary" id="again">${soloDaily ? 'Play random' : 'Play again'}</button>
      <button class="btn" id="share">Share result</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  content.querySelector('#again')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#menu')!.addEventListener('click', showMenu);
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  content.querySelector('#share')!.addEventListener('click', () => void shareSolo(st, mode));
}

function shareSolo(st: GameState, mode: Mode): Promise<void> {
  const head = soloDaily ? `Outsize ${todayUTC()} · ${mode.name}` : `Outsize · ${mode.name}`;
  const line = st.reason === 'maxed' ? `maxed it (${st.streak})` : `streak of ${st.streak}`;
  return share(`${head} — ${line}\nhttps://outsize.benrichardson.dev`);
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Result copied');
  } catch {
    toast('Could not copy');
  }
}

// ── multiplayer ──────────────────────────────────────────────────────────────

let netSend: (m: RaceMsg) => void = () => {};
let stOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: GameScreen | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code, created) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Everyone races the same countries. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  race = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        race?.setHost(isSelfHost);
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerLeft(seat);
        repaintLobby();
        refreshMultiResults();
      },
      onPeerJoin: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerReturned(seat);
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const sendStatus = n.channel<RaceMsg>('st', (data, from) => {
    if (!race) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    race.receive(data, seat);
    refreshMultiResults();
  });
  netSend = (m) => sendStatus(m);
  stOff = () => sendStatus.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = b.dataset.mode!;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  if (tally.length !== roster.length) tally = Array(roster.length).fill(0);
  const mode = modeOf(info.opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;
  const names = roster.map((p) => p.name);

  const rc = new Race({
    mode,
    seed: info.seed,
    selfSeat,
    names,
    isHost: info.isHost,
    deps: {
      send: (m) => netSend(m),
      onChange: () => refreshMultiResults(),
      onSelfEnd: () => {
        rounds?.finish();
        setTimeout(() => showMultiResults(), 700);
      },
    },
  });
  race = rc;
  setPlaying(true);

  if (selfSeat === null) {
    showMultiResults();
  } else {
    gameScreen = buildGameScreen({
      mode,
      timed: true,
      state: () => rc.state,
      msLeft: () => rc.msLeft,
      applyPick: (side) => {
        const before = rc.state;
        const o = outcome(before, side);
        rc.guess(side);
        o.over = rc.state.over;
        o.streak = rc.state.streak;
        return o;
      },
      tickModel: (dt) => {
        const before = rc.state;
        if (before.over) return null;
        const pair = current(before)!;
        rc.tick(dt);
        if (rc.state.over && before.over === false) {
          return { pair, picked: null, correct: false, over: true, streak: rc.state.streak };
        }
        return null;
      },
      onOver: () => {
        /* onSelfEnd already scheduled the results */
      },
      onQuit: () => backToLobby(),
    });
    onCleanup(() => {
      gameScreen?.destroy();
      gameScreen = null;
    });
    const cd: Countdown = runCountdown(document.body, sfx, () => {});
    onCleanup(() => cd.cancel());
  }
}

// ── multiplayer results ──────────────────────────────────────────────────────

let resultsMounted = false;
let winsAwarded = false;
const closeWait = createOneShot(20_000, () => refreshMultiResults());

function showMultiResults(): void {
  teardown();
  setPlaying(false);
  resultsMounted = true;
  winsAwarded = false;
  content.innerHTML = `
    <section class="screen results" id="mres"></section>
    <div class="menu-actions" id="mact">
      <button class="btn primary" id="again">Play again</button>
      <p class="waiting-note" id="waiting" aria-live="polite"></p>
      <button class="btn" id="lobby">Back to lobby</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  const again = content.querySelector<HTMLButtonElement>('#again')!;
  again.addEventListener('click', () => {
    rounds?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });
  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  content.querySelector('#menu')!.addEventListener('click', () => void leaveRoom());
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  onCleanup(() => {
    resultsMounted = false;
    closeWait.cancel();
  });
  closeWait.poke();
  refreshMultiResults();
}

function refreshMultiResults(): void {
  if (!resultsMounted || !race) return;
  const rc = race;
  const box = content.querySelector<HTMLElement>('#mres');
  if (!box) return;

  if (!winsAwarded && rc.everyoneDone()) {
    winsAwarded = true;
    const top = rc.ranked().find((r) => !r.left);
    if (top && top.streak > 0) {
      // A winner needs a clear top streak; ties award nobody.
      const best = rc.ranked().filter((r) => !r.left && r.streak === top.streak);
      if (best.length === 1) tally[top.seat] = (tally[top.seat] ?? 0) + 1;
    }
  }

  box.innerHTML = multiResultsHtml(rc.ranked(), rc.selfSeat, rc.mode, rc.state.seq, tally);

  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (waiting) {
    const r = rounds?.state();
    if (r && r.startsInMs !== null) {
      waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
    } else if (!rc.everyoneDone()) {
      const left = [...rc.results.values()].filter((x) => !x.over).map((x) => x.name);
      waiting.textContent = left.length ? `Still playing: ${left.join(', ')}` : '';
    } else if (r && r.votes.length) {
      waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
    } else {
      waiting.textContent = '';
    }
  }
}

function backToLobby(): void {
  teardown();
  race = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  race = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    stOff();
  } catch {
    /* channel may already be gone */
  }
  stOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ─────────────────────────────────────────────────────────────────────

function boot(): void {
  hardenViewport();
  mountShell();
  const room = new URL(location.href).searchParams.get('room');
  if (room && normalizeRoomCode(room).length >= 3) showRoomEntry();
  else showMenu();
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// A hook the browser-verification pass drives to play without synthetic pointer
// events (which do not always land a real click). Test-only.
(window as unknown as { __os?: unknown }).__os = {
  pickSolo: (side: Side): GameState | null => {
    if (!soloState) return null;
    const before = soloState;
    if (before.over) return before;
    soloState = applyGuess(before, side);
    return soloState;
  },
  pickCorrectSolo: (): GameState | null => {
    if (!soloState || soloState.over) return soloState;
    const c = correctSide(soloState)!;
    soloState = applyGuess(soloState, c);
    return soloState;
  },
  state: (): GameState | null => soloState,
  current: (): Pair | null => (soloState ? current(soloState) : null),
  bigger: (): Side | null => (soloState ? correctSide(soloState) : null),
};

boot();
